import { set } from 'lodash/fp';
import { useCallback, useState } from 'react';

/**
 * Wraps the usage of a string-based cache for a given function. It'll be keyed
 * on the inputs to the given function. If the function was already called with
 * the key, we'll get the it from the cache. Otherwise, we'll call the function.
 *
 * @param fn the function to execute
 * @returns wrapped is the wrapped function with cache.
 *
 * clearCache: call to clear the cache
 */
export function useCache<T, K extends string | number>(fn: (key: K) => T) {
  const [cache, setCache] = useState({} as Record<K, T>);

  const wrapped = useCallback(
    (key: K) => {
      if (key in cache) {
        return cache[key];
      }

      const newOptions = fn(key);

      setCache(set(key, newOptions));
      return newOptions;
    },
    [cache, fn],
  );

  const clearCache = () => setCache({} as Record<K, T>);

  return { wrapped, clearCache, setCache };
}
